import AutoModeIcon from '@mui/icons-material/AutoMode';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import { css, styled as muiStyled } from '@mui/material/styles';
import React from 'react';

import { stringAvatar } from '../../assessment/helper';
import { getDesignTokens } from '../../base/core';
import {
  GenericCard,
  GenericCardContent,
  GenericCardHeader,
} from '../../base/generic-card';
import { UserPlus } from '../../base/icon/user-plus';
import StatusBadge from '../../base/status-badge';
import Text from '../../base/text';
import ProgressWithText from '../../progress-with-text';

import * as GQL from '~/graphql/generated/client';
import AssessmentStatusText from '~c/components/assessment/assessment-status';

interface AssessmentCardProps {
  className?: string;
  entry: GQL.AssessmentFragment;
  onClick?: (entry: GQL.AssessmentFragment) => void;
  ownerEmail?: string;
  showActions?: boolean;
  isAdmin?: boolean;
  onAssign?: (assessmentId: string) => void;
  onAnswerGen?: (entry: GQL.AssessmentFragment) => void;
  onRemove?: (assessmentId: string) => void;
  loadingAssessmentId?: string | null;
  removeLoading?: boolean;
}

const AssessmentCardImpl: React.FC<AssessmentCardProps> = ({
  className,
  entry,
  onClick,
  ownerEmail,
  showActions,
  isAdmin,
  onAssign,
  onAnswerGen,
  onRemove,
  loadingAssessmentId,
  removeLoading,
}) => {
  const theme = useTheme();
  const policyName = entry.policy?.name ?? entry.name ?? '';
  const hasAssignments =
    entry.assignments?.length && entry.assignments.length > 0;

  const handleClick = () => {
    onClick?.(entry);
  };

  const handleAssign = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAssign?.(entry._id);
  };

  const handleAnswerGen = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAnswerGen?.(entry);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.(entry._id);
  };

  return (
    <GenericCard
      variant="minimal"
      className={className}
      size="custom"
      width="100%"
      onClick={handleClick}
    >
      <GenericCardHeader
        title={
          <Box className="card-title-row">
            <Text sz="bold-body16" className="policy-name">
              {policyName}
            </Text>
            <KeyboardArrowRightIcon className="arrow-icon" />
          </Box>
        }
        action={null}
      />
      <GenericCardContent>
        <Box className="card-body">
          <Box className="stakeholder-row">
            <Text sz="body12" color="secondary">
              Stakeholder(s):
            </Text>
            <Box className="avatars-container">
              {hasAssignments ? (
                entry.assignments?.slice(0, 3).map((a: any) => {
                  const email = a?.assignedTo || '';
                  const fullName = (a as any)?.assignedToName || '';
                  const tooltipText = fullName
                    ? `${fullName} (${email})`
                    : email;
                  return (
                    <Tooltip key={email} title={tooltipText} arrow>
                      <Avatar {...stringAvatar(fullName, theme)} />
                    </Tooltip>
                  );
                })
              ) : (
                <Tooltip title={ownerEmail ?? ''} arrow>
                  <Avatar
                    sx={{
                      backgroundColor: (t) => t.palette.background.default,
                      color: (t) => t.palette.text.primary,
                      fontWeight: 'bold',
                      width: 32,
                      height: 32,
                      fontSize: '12px',
                    }}
                  >
                    {ownerEmail
                      ? ownerEmail
                          .split('@')[0]
                          .split('.')
                          .map((part) => part.charAt(0).toUpperCase())
                          .join('')
                          .substring(0, 2)
                      : 'NA'}
                  </Avatar>
                </Tooltip>
              )}
              {hasAssignments && (entry.assignments?.length ?? 0) > 3 && (
                <Text sz="body12" color="secondary">
                  +{(entry.assignments?.length ?? 0) - 3}
                </Text>
              )}
            </Box>
          </Box>

          <Box className="status-row">
            <StatusBadge
              variant="alignment"
              value={
                entry.riskStatus === GQL.AssessmentRiskStatusType.Low
                  ? 'fully-compliant'
                  : entry.riskStatus === GQL.AssessmentRiskStatusType.Medium
                    ? 'partially-compliant'
                    : 'not-compliant'
              }
            />
            <AssessmentStatusText
              status={entry?.status}
              assessmentID={entry._id}
              allowed={false}
            />
          </Box>

          <Box className="progress-row">
            <ProgressWithText value={entry?.completionPercent} />
          </Box>

          {showActions && (
            <Box className="actions-row">
              <Tooltip title="Assign responders">
                <IconButton
                  color="primary"
                  onClick={handleAssign}
                  size="small"
                  sx={{ color: 'text.primary' }}
                >
                  <UserPlus />
                </IconButton>
              </Tooltip>
              {isAdmin && (
                <Tooltip
                  title={
                    loadingAssessmentId === entry._id
                      ? 'Generating...'
                      : 'Answer Generation'
                  }
                >
                  <span>
                    <IconButton
                      color="primary"
                      onClick={handleAnswerGen}
                      size="small"
                      disabled={loadingAssessmentId === entry._id}
                      sx={{ color: 'text.primary' }}
                    >
                      {loadingAssessmentId === entry._id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AutoModeIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Remove assessment">
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleRemove}
                    size="small"
                    disabled={removeLoading}
                    sx={{ color: 'text.primary' }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      </GenericCardContent>
    </GenericCard>
  );
};

const AssessmentCard = muiStyled(AssessmentCardImpl)(({ theme }) => {
  const t = getDesignTokens(theme.palette.mode, theme.accentPalette);
  return css`
    &.MuiCard-root {
      border: 1px solid ${t.borderSubtle};
      width: 100%;
      cursor: pointer;
      transition: border-color 0.2s ease;

      &:hover {
        border-color: ${t.borderDefault};
      }

      &:active {
        background-color: ${t.surface2};
      }
    }

    .card-title-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }

    .policy-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .arrow-icon {
      color: ${t.textSecondary};
      flex-shrink: 0;
    }

    .card-body {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(1.5)};
    }

    .stakeholder-row {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(1)};
    }

    .avatars-container {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(0.5)};
    }

    .avatars-container .MuiAvatar-root {
      border: 1px solid ${theme.palette.divider};
    }

    .status-row {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(2)};
      flex-wrap: wrap;
    }

    .progress-row {
      width: 100%;
    }

    .actions-row {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(0.5)};
      padding-top: ${theme.spacing(1)};
      border-top: 1px solid ${t.borderSubtle};
    }
  `;
});

export default AssessmentCard;
